// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module useAnalyticsDashboard
 * @package @geenius/analytics/react-css
 * @description Normalizes dashboard page inputs into a stable React state
 * object for hosts that compose the React CSS analytics dashboard manually.
 */

import type {
  AnalyticsDashboardData,
  AnalyticsEvent,
  CohortAnalysis,
  FunnelResult,
  RealtimeStats,
} from "@geenius/analytics";
import { useMemo } from "react";
import type { AnalyticsErrorStateValue } from "../components/ErrorState";
import type { AnalyticsDashboardPageProps } from "../pages/AnalyticsDashboardPage";

/** Input data consumed by {@link useAnalyticsDashboard}. */
export interface UseAnalyticsDashboardOptions {
  data?: AnalyticsDashboardData | null;
  recentEvents?: AnalyticsEvent[];
  realtimeStats?: RealtimeStats | null;
  funnelResult?: FunnelResult | null;
  cohortAnalysis?: CohortAnalysis | null;
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
}

/** Numeric dashboard summary derived by {@link useAnalyticsDashboard}. */
export interface AnalyticsDashboardSummary {
  totalPageViews: number;
  uniqueVisitors: number;
  topPageCount: number;
  recentEventCount: number;
  activeVisitors: number;
}

/** Reactive dashboard state returned by {@link useAnalyticsDashboard}. */
export interface UseAnalyticsDashboardReturn {
  data: AnalyticsDashboardData | null;
  recentEvents: AnalyticsEvent[];
  realtimeStats: RealtimeStats | null;
  funnelResult: FunnelResult | null;
  cohortAnalysis: CohortAnalysis | null;
  isLoading: boolean;
  error: AnalyticsErrorStateValue;
  hasDashboardData: boolean;
  hasRealtimeData: boolean;
  isEmpty: boolean;
  summary: AnalyticsDashboardSummary;
  pageProps: Pick<
    AnalyticsDashboardPageProps,
    | "cohortAnalysis"
    | "data"
    | "error"
    | "funnelResult"
    | "isLoading"
    | "recentEvents"
    | "realtimeStats"
  >;
}

/**
 * Builds a stable dashboard view model for the React CSS dashboard component.
 *
 * @param options Dashboard data, realtime stats, events, and state flags.
 * @returns Normalized dashboard state and page-ready props.
 */
export function useAnalyticsDashboard(
  options: UseAnalyticsDashboardOptions = {},
): UseAnalyticsDashboardReturn {
  const data = options.data ?? null;
  const recentEvents = options.recentEvents ?? [];
  const realtimeStats = options.realtimeStats ?? null;
  const funnelResult = options.funnelResult ?? null;
  const cohortAnalysis = options.cohortAnalysis ?? null;
  const isLoading = options.isLoading ?? false;
  const error = options.error ?? null;

  const summary = useMemo<AnalyticsDashboardSummary>(
    () => ({
      activeVisitors: realtimeStats?.activeVisitors ?? 0,
      recentEventCount: recentEvents.length,
      topPageCount: data?.topPages.length ?? 0,
      totalPageViews: data?.totalPageViews ?? 0,
      uniqueVisitors: data?.uniqueVisitors ?? 0,
    }),
    [data, recentEvents.length, realtimeStats],
  );

  const pageProps = useMemo<UseAnalyticsDashboardReturn["pageProps"]>(
    () => ({
      cohortAnalysis,
      data,
      error,
      funnelResult,
      isLoading,
      recentEvents,
      realtimeStats,
    }),
    [
      cohortAnalysis,
      data,
      error,
      funnelResult,
      isLoading,
      recentEvents,
      realtimeStats,
    ],
  );

  return useMemo(
    () => ({
      cohortAnalysis,
      data,
      error,
      funnelResult,
      hasDashboardData:
        data !== null &&
        (data.totalPageViews > 0 ||
          data.uniqueVisitors > 0 ||
          data.topPages.length > 0),
      hasRealtimeData: realtimeStats !== null,
      isEmpty:
        !isLoading &&
        !error &&
        data === null &&
        realtimeStats === null &&
        recentEvents.length === 0 &&
        funnelResult === null &&
        cohortAnalysis === null,
      isLoading,
      pageProps,
      realtimeStats,
      recentEvents,
      summary,
    }),
    [
      cohortAnalysis,
      data,
      error,
      funnelResult,
      isLoading,
      pageProps,
      realtimeStats,
      recentEvents,
      summary,
    ],
  );
}
